import { buildId } from "@oysterhr/common"
import {
  companySalaryInsightsCheckoutPath,
  companySalaryInsightsToolPath,
  SalaryInsightsQueryParams,
} from "@oysterhr/compensation-tools"
import { useSalaryInsightsPlansAndPermissions } from "@oysterhr/compensation-tools/src/hooks/useSalaryInsightsPlansAndPermissions"
import { MonetizationBanner } from "@oysterhr/monetization"
import { Company } from "@oysterhr/schema"
import { LoadingSpinner } from "@oysterhr/ui"
import { useTranslation } from "react-i18next"
import { useParams, useSearchParams } from "react-router-dom"
import { amplitudeLogEvent } from "~/app/utils/amplitudeHelpers"

interface CalculationGridBannerProps {
  numberOfCalculations: number
  activeCalculationCountry: string
}

export const CalculationGridBanner = ({
  numberOfCalculations,
  activeCalculationCountry,
}: CalculationGridBannerProps) => {
  const { t } = useTranslation("CostCalculator")
  const { t: paidPlanBannerTranslation } = useTranslation("CostCalculator", {
    keyPrefix: "monetizationBanner.subscriptionBanner",
  })
  const { t: trialBannerTranslation } = useTranslation("CostCalculator", {
    keyPrefix: "monetizationBanner.trialBanner",
  })

  const [searchParams] = useSearchParams()
  const { companyStrongId } = useParams()

  const { expiredSalaryInsightsTrialAddOn, salaryInsightsTrialAvailable, loading } =
    useSalaryInsightsPlansAndPermissions(buildId<Company>("Company", companyStrongId))

  const redirectToSalaryInsightsCheckoutPageTracking = () => {
    amplitudeLogEvent("CA-click-subscription-banner-cost-calculator")
  }
  const redirectToSalaryInsightsPageTracking = () => {
    amplitudeLogEvent("CA-click-trial-banner-cost-calculator")
  }

  const companySalaryInsightsCheckoutLink = companySalaryInsightsCheckoutPath({
    companyId: companyStrongId,
    query: Object.fromEntries(searchParams) as SalaryInsightsQueryParams,
  })
  const companySalaryInsightsTrialLink = companySalaryInsightsToolPath({
    companyId: companyStrongId,
  })

  const isSingleCalculation = numberOfCalculations === 1
  const country =
    isSingleCalculation && activeCalculationCountry ? ` in ${activeCalculationCountry}` : ""
  const headingText = t("monetizationBanner.title", { country: country })

  if (loading) {
    return <LoadingSpinner />
  }
  if (expiredSalaryInsightsTrialAddOn) {
    return (
      <MonetizationBanner
        backgroundGradient="bg-gradient-delightful"
        image="/images/cost-index.png"
        alt="salary-insights"
        href={companySalaryInsightsCheckoutLink}
        buttonAction={redirectToSalaryInsightsCheckoutPageTracking}
        dataAnalyticsName="CA-click-subscription-banner-cost-calculator"
        t={paidPlanBannerTranslation}
        customText={headingText}
      />
    )
  }
  if (salaryInsightsTrialAvailable) {
    return (
      <MonetizationBanner
        backgroundGradient="bg-gradient-calm"
        image="/images/cost-index.png"
        alt="salary-insights"
        href={companySalaryInsightsTrialLink}
        buttonAction={redirectToSalaryInsightsPageTracking}
        dataAnalyticsName="CA-click-trial-banner-cost-calculator"
        t={trialBannerTranslation}
        customText={headingText}
      />
    )
  } else return null
}
